<template>
  <div v-show="validBuildingParameters" class="building" :style="{ height: buildingHeight }" ref="building">
    <expand-building-button v-if="actionType !== 'view'" class="add-floor-button" @click="$emit('add-ground-floor')" />

    <roof :roof-width="roofWidth"></roof>

    <div style="display: flex; flex-direction: row" :style="{ 'margin-left': actionType !== 'view' ? '0px' : '36px' }">
      <expand-building-button v-if="actionType !== 'view'" :style="{ top: addColumnButtonPosition }" style="transform: rotate(270deg)" @click="addColumn" />

      <div class="floorbox-columns">
        <div
          v-for="column in columns"
          :key="column.color"
          class="floorbox-column"
          :style="{
            height: columnHeight + 'px',
            backgroundColor:
              selectedObjectIsColumn && selectedObject.index === column.index
              ? '#9f9f9f'
              : '#8a8a8a'
          }"
          @click="$emit('select-column', column.index)">
          <div
            v-for="(floorbox, fbIndex) in column.floorboxes.length > 3 ? column.floorboxes.slice(0, 2) : column.floorboxes"
            :key="floorbox.color"
            :id="'col-' + column.index + '-fb-' + fbIndex"
            class="floorbox"
            :style="{ backgroundColor: floorbox.color, border: selectedFloorbox.label === floorbox.label ? '1px red solid' : 'none' }"
            @click.stop="selectFloorbox(floorbox, column.index)"
          />
          <div v-if="column.floorboxes.length > 3" class="floorbox ellipsis">
            <span>...</span>
            <div class="floorbox-tooltip" :style="{ 'margin-left': -(8 * column.floorboxes.length - 4) + 'px' }">
              <div
                v-for="(floorbox, floorboxIndex) in column.floorboxes"
                :key="floorbox.color" class="floorbox"
                @click.stop="$emit('swap-floorbox', { columnIndex: column.index, floorboxIndex: floorboxIndex })"
                :style="{ 'background-color': floorbox.color }"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="upper-building">
          <div
            v-for="(floor, floorIndex) in groundFloors"
            :key="floorIndex"
            :id="'g-floor-' + (groundFloors.length -1 - floorIndex)"
            class="building-floor"
            :style="{
              filter: selectedObjectIsFloor && floorIndex === selectedObject.index
              ? 'brightness(0.7)'
              : 'brightness(1)',
              width: floorWidth + 'px'
            }"
            @click="selectFloor(floorIndex, 'g')"
          >
            <div class="fraction-container">
              <div v-for="(fraction, fractionIndex) in floor.fractions.length > 4 ? floor.fractions.slice(0, 4) : floor.fractions" :key="fractionIndex">
                <div
                  class="fraction"
                  :id="'g-floor-' + (groundFloors.length -1 - floorIndex) + '-fraction-' + fraction"
                  @click.stop="$emit('select-fraction', {
                    floorIndex: floorIndex,
                    floorType: 'g',
                    fractionIndex: fractionIndex
                  })"
                >
                  <span v-if="floor.fractions.length > 4 && fractionIndex === 3">
                    +{{ floor.fractions.length - 3 }}
                  </span>
                </div>
                <div
                  class="fraction-floorbox"
                  :style="{ background: fraction.floorbox_color }"
                />
              </div>
            </div>
            <building-side :is-building-entrance-floor="floorIndex === 0" :key="selectedFloorbox.uuid" :selected-floorbox="selectedFloorbox" @click="connectAllFractions(floorIndex, 'g')" />
          </div>
        </div>


        <div class="lower-building">
          <div
            v-for="(floor, floorIndex) in undergroundFloors"
            :key="floorIndex"
            :id="'u-floor-' + floorIndex"
            class="building-floor"
            :style="{
              filter: selectedObjectIsFloor && -(floorIndex) -1 === selectedObject.index
              ? 'brightness(0.7)'
              : 'brightness(1)',
              width: floorWidth + 'px'
            }"
            @click="selectFloor(floorIndex, 'u')"
          >
            <div class="fraction-container">
              <div v-for="(fraction, fractionIndex) in floor.fractions.length > 4 ? floor.fractions.slice(0, 4) : floor.fractions" :key="fractionIndex">
                <div
                  class="fraction"
                  :id="'u-floor-' + floorIndex + '-fraction-' + fraction"
                  @click.stop="$emit('select-fraction', {
                    floorIndex: floorIndex,
                    floorType: 'u',
                    fractionIndex: fractionIndex
                  })"
                >
                  <span v-if="floor.fractions.length > 4 && fractionIndex === 3">
                    +{{ floor.fractions.length - 3 }}
                  </span>
                </div>
                <div
                  class="fraction-floorbox"
                  :style="{ background: fraction.floorbox_color }"
                />
              </div>
            </div>
            <building-side :selected-floorbox="selectedFloorbox" :key="selectedFloorbox.uuid" @click="connectAllFractions(floorIndex, 'u')"/>
          </div>
        </div>
      </div>
    </div>

    <expand-building-button v-if="actionType !== 'view'" style="transform: rotate(180deg)" class="add-floor-button" @click="$emit('add-underground-floor')" />

    <div class="ground-divider" :style="{ 'margin-top': floorPosition, width: groundWidth + 'px' }" />
    <div class="ground" :style="{ width: groundWidth + 'px', height: groundHeight + 'px' }" />
  </div>
</template>

<script>
import ExpandBuildingButton from './ExpandBuildingButton';
import Roof from './BuildingComponents/Roof';
import BuildingSide from './BuildingComponents/Side';

export default {
  name: "Building",

  components: {
    ExpandBuildingButton,
    Roof,
    BuildingSide
  },

  props: {
    groundFloors: {
      type: Array,
      required: true
    },

    undergroundFloors: {
      type: Array,
      required: true
    },

    columns: {
      type: Array,
      required: true
    },

    selectedFloorbox: {
      type: Object,
      required: false
    },

    selectedObject: {
      type: Object
    },

    actionType: {
      type: String
    }
  },

  data() {
    return {
      heightOfFloor: 40,
      groundDividerHeight: 10,
      columnWidth: 16,
      expandButtonsSize: 36,
    }
  },

  computed: {
    /** Indicates the width of the building's roof */
    roofWidth() {
      let floors = this.mostFractionsInFloor > 4 ? 4 : this.mostFractionsInFloor;
      return (floors * 28 + 39 + 4)
        + (this.columns.length * this.columnWidth);
    },

    /** Indicates the height of the building's columns */
    columnHeight() {
      return ((this.drawnGroundFloors + this.drawnUndergroundFloors) * this.heightOfFloor) + this.groundDividerHeight;
    },

    /** Indicates the height of the building */
    buildingHeight() {
      return this.columnHeight + 107 + 'px';
    },

    /** Indicates the position of the floor in relation to the building */
    floorPosition() {
      let baseValue = 0 - (this.drawnUndergroundFloors * this.heightOfFloor + this.groundDividerHeight)
      return this.actionType !== 'view'
        ? (baseValue - this.expandButtonsSize) + 'px'
        : baseValue + 'px';
    },

    /** Indicates the width of the ground */
    groundWidth() {
      return this.roofWidth + 70;
    },

    /** Indicates the height of the ground */
    groundHeight() {
      return (this.drawnUndergroundFloors * 40) + this.expandButtonsSize;
    },

    /** Indicates the position of the button to add a column */
    addColumnButtonPosition() {
      return (this.drawnGroundFloors - 1) * this.heightOfFloor + 'px'
    },

    /** Indicates the number of ground floors to be drawn */
    drawnGroundFloors() {
      return this.groundFloors.length > 7 ? 7 : this.groundFloors.length;
    },

    /** Indicates the number of underground floors to be drawn */
    drawnUndergroundFloors() {
      return this.undergroundFloors.length > 3 ? 3 : this.undergroundFloors.length;
    },

    /** Indicates if the building structure parameters are valid */
    validBuildingParameters() {
      return this.groundFloors.length > 0 || this.undergroundFloors.length > 0
    },

    /** Indicates the maximum number of fractions in a single floor of the building */
    mostFractionsInFloor() {
      let max = -1;
      this.groundFloors.forEach(floor => { if (floor.fractions.length > max) max = floor.fractions.length });
      this.undergroundFloors.forEach(floor => { if (floor.fractions.length > max) max = floor.fractions.length });
      return max;
    },

    /** Indicates if the selected object is a column */
    selectedObjectIsColumn() {
      return this.selectedObject.type === "Column"
    },

    /** Indicates if the selected object is a floor */
    selectedObjectIsFloor() {
      return this.selectedObject.type === "Floor"
    },

    /** Indicates the width of the building's floors */
    floorWidth() {
      return this.roofWidth - (this.columns.length * this.columnWidth)
    },

    /** Indicates if the action type is 'view' */
    actionTypeIsView() {
      return this.actionType === 'view'
    }
  },

  methods: {
    /** Emits an event to add a column to the building if the number of columns is lower than 10 */
    addColumn() {
      if (this.columns.length < 10) {
        this.$emit('add-column')
      }
    },

    /**
     * Selects a floorbox
     * If the user is viewing the building data it selects the floorbox column, else it selects the floorbox
     *
     * @param id - the id of the selected floorbox
     * @param color - the color of the selected floorbox
     * @param label - the label of the selected floorbox
     * @param uuid - the uuid of the selected floorbox
     * @param columnIndex - the index of the floorbox's column
     */
    selectFloorbox(floorbox, columnIndex) {
      if (this.actionTypeIsView) {
        this.$emit('select-column', columnIndex)
      } else {
        const hasClosure = floorbox.hasOwnProperty('closure');
        this.$emit('select-floorbox', {
          floorbox: {
            id: floorbox.id,
            color: floorbox.color,
            label: floorbox.label,
            uuid: hasClosure ? floorbox.closure.cl_uuid : floorbox.uuid,
            cable_out_material_id: floorbox.cable_out_material_id,
            cable_out_owner_id: floorbox.cable_out_owner_id
          }
        })
      }
    },

    /**
     * Emits an event to connect all fractions to the selected floorbox
     *
     * @param floorIndex - the floor of the fractions
     * @param floorType - the initial of the type of the floor (ground or underground)
     */
    connectAllFractions(floorIndex, floorType) {
      this.$emit('connect-all-fractions', {
        floorIndex: floorIndex,
        floorType: floorType
      })
    },

    /**
     * Emits an evenet to select the floor
     *
     * @param index - the index of the floor
     * @param type - the initial of the type of the floor (ground or underground)
     */
    selectFloor(index, type) {
      this.$emit('select-floor', {
        floorIndex: index,
        floorType: type
      })
    }
  }
}
</script>

<style scoped>
.building-floor {
  height: 40px;
  background-color: #d4d4d4;
  border-bottom: 2px solid #5d5d5d;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: 4px;
  cursor: pointer;
  justify-content: space-between;
}

.fraction {
  height: 25px;
  width: 20px;
  background-color: #b3b3b3;
  margin: 0 4px;
  text-align: center;
  line-height: 27px;
}

.ground-divider {
  height: 10px;
  background: linear-gradient(to right,
      #c4c4c4 15%,
      #a8a8a8 15% 30%,
      #b9b9b9 30% 45%,
      #a8a8a8 45% 70%,
      #c4c4c4 70% 85%,
      #cfcfcf 85%);
}

.ground {
  background: #686868;
}

.upper-building {
  max-height: 280px;
  overflow-y: scroll;
  display: flex;
  flex-direction: column-reverse;
}

.upper-building .building-floor {
  background-color: #d4d4d490;
}

.upper-building .fraction {
  background-color: #b3b3b38b;
}

.upper-building .building-side {
  opacity: 95%;
}

.lower-building {
  max-height: 120px;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  margin-top: 10px
}

.add-floor-button {
  margin-left: 45%;
}

.floorbox-column {
  background-color: #8a8a8a;
  width: 16px;
  height: 300px;
  border-right: 1px solid #5d5d5d;
  border-left: 1px solid #5d5d5d;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.floorbox-columns {
  display: flex;
  flex-direction: row-reverse;
}

.floorbox-tooltip>.floorbox+.floorbox {
  margin-left: 4px;
}

.floorbox-tooltip>.floorbox {
  border: 1px grey solid;
}

.floorbox.ellipsis {
  position: relative;
  display: inline-block;
  background-color: lightgray;
}

.floorbox.ellipsis .floorbox-tooltip {
  visibility: hidden;
  width: fit-content;
  background-color: #fff;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  border: 1px #555 solid;
  padding: 4px 5px 0px 5px;
  position: absolute;
  z-index: 1;
  top: 125%;
  opacity: 0;
  transition: opacity 0.3s;
  display: flex;
  flex-direction: row;
}

.floorbox.ellipsis:hover .floorbox-tooltip {
  visibility: visible;
  opacity: 1;
}

.floorbox.ellipsis .floorbox-tooltip::after {
  content: "";
  position: absolute;
  bottom: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent #fff transparent;
}

.fraction-floorbox {
  height: 5px;
  width: 20px;
  border: 1px grey solid;
  margin: 0 4px;
}

.fraction-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
}

.floorbox {
  width: 11px;
  height: 11px;
  border-radius: 2px;
  text-align: center;
  line-height: 10px;
  font-size: 9px;
  margin-bottom: 5px;
  cursor: pointer;
}

</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Buildings/Vertical/Building.vue